import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";

import Breadcrumbs from "../../../../components/BreadCrumbs";
import CreateForm from "../../../../components/Events/Form/Form";
import { COMPANY_DRAFT_EVENT_STEP_IDS } from "../../../../components/Events/Form/formStepIds";
import messages from "../../../../assets/locale/messages";
import * as companyPortalEventsApi from "../../../../network/apis/companyPortalEvents";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import { showErrorMsg, showSuccessMsg } from "../../../../utils/Helpers";
import {
  COMPANY_PORTAL_FORM_VARIANT,
  getPortalApiErrorMessage,
  mergePortalEventTypeIntoPayload,
  unwrapPortalEventEnvelope,
} from "../../../../utils/companyPortal/eventApi";
import {
  mergeEventSnapshot,
  useCompanyPortalLookups,
  useLeaveWhenNotDraft,
  useScheduleBounds,
} from "../_eventFormHelpers";

const COMPANY_EVENT_DRAFT_ID_KEY = "companyPortalCreateEventDraftId";

const saveDraftId = (id) => {
  if (!id) return;
  try {
    window.sessionStorage.setItem(COMPANY_EVENT_DRAFT_ID_KEY, String(id));
  } catch (_e) {
    // sessionStorage may be unavailable in private modes; URL restore still works.
  }
};

const readDraftId = () => {
  try {
    return window.sessionStorage.getItem(COMPANY_EVENT_DRAFT_ID_KEY);
  } catch (_e) {
    return null;
  }
};

const clearDraftId = () => {
  try {
    window.sessionStorage.removeItem(COMPANY_EVENT_DRAFT_ID_KEY);
  } catch (_e) {
    // no-op
  }
};

const CreateEvent = () => {
  const history = useHistory();
  const location = useLocation();
  const { id: routeEventId } = useParams();
  const lang = useSelector((state) => state.locale.lang);
  const shared = messages[lang]?.shared ?? messages.en.shared;
  const listEvents = messages[lang]?.events?.listEvents ?? {};

  const eventIdRef = useRef(null);
  const isPageReloadingRef = useRef(false);
  /** Kept out of the restore-draft effect's deps so a language switch doesn't re-trigger it. */
  const generalErrMsgRef = useRef(shared.generalErrMsg);
  generalErrMsgRef.current = shared.generalErrMsg;
  const [initialEvent, setInitialEvent] = useState(null);
  const [draftLoading, setDraftLoading] = useState(false);
  const [nextLoading, setNextLoading] = useState(false);

  const { rolesOptions, requirementsOptions, eventTypeOptions } =
    useCompanyPortalLookups();
  const { scheduleBounds, updateScheduleBounds } = useScheduleBounds();

  const syncDraftIdToUrl = useCallback(
    (id) => {
      if (!id) return;
      const search = new URLSearchParams(location.search);
      if (search.get("event_id") === String(id)) return;
      search.set("event_id", String(id));
      history.replace({
        pathname: location.pathname,
        search: `?${search.toString()}`,
      });
    },
    [history, location.pathname, location.search],
  );

  const { leaveIfNotDraft, leaveIfPublishedMeanwhile } = useLeaveWhenNotDraft({
    eventIdRef,
    onLeave: clearDraftId,
  });

  const saveEventBody = useCallback(
    async (body) => {
      const payload = mergePortalEventTypeIntoPayload(
        body && typeof body === "object" ? { ...body } : {},
        initialEvent,
      );
      if (!eventIdRef.current) {
        const res = await companyPortalEventsApi.createCompanyPortalEvent(payload);
        const unwrapped = unwrapPortalEventEnvelope(res);
        const id = unwrapped?.id;
        if (!id) {
          throw new Error(getPortalApiErrorMessage(null, shared.generalErrMsg));
        }
        eventIdRef.current = id;
        saveDraftId(id);
        syncDraftIdToUrl(id);
        updateScheduleBounds(unwrapped?.attributes ?? {}, payload);
        setInitialEvent((current) => mergeEventSnapshot(current, unwrapped));
        return unwrapped;
      }

      const existing = eventIdRef.current;
      const res = await companyPortalEventsApi.updateCompanyPortalEvent({
        id: existing,
        data: payload,
      });
      const unwrapped = unwrapPortalEventEnvelope(res);
      updateScheduleBounds(unwrapped?.attributes ?? {}, payload);
      setInitialEvent((current) => mergeEventSnapshot(current, unwrapped));
      return unwrapped ?? { id: existing, attributes: {} };
    },
    [initialEvent, shared.generalErrMsg, syncDraftIdToUrl, updateScheduleBounds],
  );

  useEffect(() => {
    const urlEventId = new URLSearchParams(location.search).get("event_id");
    const storedEventId = routeEventId ? null : readDraftId();
    const eventId = routeEventId || urlEventId || storedEventId;
    if (!eventId) return undefined;

    let cancelled = false;
    eventIdRef.current = eventId;
    saveDraftId(eventId);
    if (!urlEventId) {
      syncDraftIdToUrl(eventId);
    }

    companyPortalEventsApi
      .getCompanyPortalEvent(eventId)
      .then((res) => {
        if (cancelled) return;
        const unwrapped = unwrapPortalEventEnvelope(res);
        if (leaveIfNotDraft(unwrapped?.attributes)) return;
        setInitialEvent(unwrapped?.attributes ?? null);
        updateScheduleBounds(unwrapped?.attributes ?? {}, {});
      })
      .catch((e) => {
        if (cancelled) return;
        eventIdRef.current = null;
        clearDraftId();
        showErrorMsg(getPortalApiErrorMessage(e, generalErrMsgRef.current));
      });

    return () => {
      cancelled = true;
    };
  }, [leaveIfNotDraft, location.search, syncDraftIdToUrl, updateScheduleBounds]);

  useEffect(() => {
    const markPageReloading = () => {
      isPageReloadingRef.current = true;
    };

    window.addEventListener("beforeunload", markPageReloading);

    return () => {
      window.removeEventListener("beforeunload", markPageReloading);
      if (!isPageReloadingRef.current) {
        clearDraftId();
      }
    };
  }, []);

  const handlePersistDraft = useCallback(
    async ({ payload }) => {
      if (!payload) return;
      setDraftLoading(true);
      try {
        const persisted = await saveEventBody(payload);
        if (persisted) {
          queueMicrotask(() => {
            history.push(ROUTE_PATHS.companyEvents);
          });
        }
        return persisted;
      } catch (e) {
        if (!(await leaveIfPublishedMeanwhile())) {
          showErrorMsg(getPortalApiErrorMessage(e, shared.generalErrMsg));
        }
        return null;
      } finally {
        setDraftLoading(false);
      }
    },
    [history, leaveIfPublishedMeanwhile, saveEventBody, shared.generalErrMsg],
  );

  const handleAdvanceAfterValidatedStep = useCallback(
    async ({ stepIndex, payload }) => {
      if (stepIndex > 0 && !eventIdRef.current) {
        showErrorMsg(shared.generalErrMsg);
        return false;
      }
      setNextLoading(true);
      try {
        return await saveEventBody(payload ?? {});
      } catch (e) {
        if (!(await leaveIfPublishedMeanwhile())) {
          showErrorMsg(getPortalApiErrorMessage(e, shared.generalErrMsg));
        }
        return null;
      } finally {
        setNextLoading(false);
      }
    },
    [leaveIfPublishedMeanwhile, saveEventBody, shared.generalErrMsg],
  );

  const handleFinishPublish = useCallback(
    async ({ payload }) => {
      setNextLoading(true);
      try {
        const persisted = await saveEventBody(payload ?? {});
        const id = persisted?.id ?? eventIdRef.current;
        if (!id) {
          showErrorMsg(shared.generalErrMsg);
          return false;
        }
        // The company no longer publishes directly: Publish & Pay raises the request (PROLINE-4016).
        clearDraftId();
        showSuccessMsg("eventUpdatedSuccessfully", false);
        // Let `CreateForm` close the publish modal (`finally`) before navigating away.
        queueMicrotask(() => {
          history.push(ROUTE_PATHS.companyEvents);
        });
        return true;
      } catch (e) {
        if (!(await leaveIfPublishedMeanwhile())) {
          showErrorMsg(getPortalApiErrorMessage(e, shared.generalErrMsg));
        }
        return false;
      } finally {
        setNextLoading(false);
      }
    },
    [history, leaveIfPublishedMeanwhile, saveEventBody, shared.generalErrMsg],
  );

  const handlePaymentSubmitted = useCallback(() => {
    clearDraftId();
    // Replace, so the browser's Back does not reopen Publish & Pay for a submitted event.
    history.replace(ROUTE_PATHS.companyEvents);
  }, [history]);

  return (
    <>
      <Breadcrumbs
        list={[
          { label: listEvents.listEventsBreadcrumb, path: ROUTE_PATHS.companyEvents },
          { label: listEvents.createEvent, path: "" },
        ]}
        BreadCrumbHasBackIcon={false}
      />
      <CreateForm
        variant={COMPANY_PORTAL_FORM_VARIANT}
        stepIds={COMPANY_DRAFT_EVENT_STEP_IDS}
        rolesOptions={rolesOptions}
        requirementsOptions={requirementsOptions}
        supervisorsOptions={[]}
        eventTypeOptions={eventTypeOptions}
        eventScheduleBounds={scheduleBounds}
        initialEvent={initialEvent}
        initialStep={location.state?.initialStep}
        requestsPortal="company"
        onPersistDraft={handlePersistDraft}
        onPersistStepContinue={handleAdvanceAfterValidatedStep}
        onFinishPublish={handleFinishPublish}
        onPaymentSubmitted={handlePaymentSubmitted}
        // The backend already toasts its reason; only leave if the event moved on.
        onPaymentRequestFailed={() => leaveIfPublishedMeanwhile(null)}
        draftLoading={draftLoading}
        nextLoading={nextLoading}
      />
    </>
  );
};

export default CreateEvent;
